import React, { useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiCpu, FiX, FiRefreshCw } from 'react-icons/fi';

const TONES = [
  { id: 'Friendly', label: 'Friendly' },
  { id: 'Professional', label: 'Professional' },
  { id: 'Concise', label: 'Concise' },
  { id: 'Persuasive', label: 'Persuasive' },
  { id: 'Formal', label: 'Formal' },
];

const AIWhatsAppHelper = ({ text, onClose, onApplyText }) => {
  const initialText = useMemo(() => text || '', [text]);
  const [tone, setTone] = useState('Friendly');
  const [value, setValue] = useState(initialText);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const handleRephrase = async () => {
    if (!value.trim()) {
      toast.error('Write WhatsApp body text first');
      return;
    }
    setLoading(true);
    setError('');
    setResult('');
    try {
      const res = await axios.post('/api/ai/rephrase-whatsapp-text', { text: value, tone });
      setResult(res.data?.result?.rephrasedText || '');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'AI request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <FiCpu className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-white font-bold">AI WhatsApp Assistant</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-all"
            type="button"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <p className="text-sm text-neutral-600">
            Rephrase WhatsApp body text (the <span className="font-semibold">document_text</span> parameter) into a
            cleaner, shorter message.
          </p>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">Current text</label>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[140px]"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Tone</label>
              <select
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              >
                {TONES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                disabled={loading}
                onClick={handleRephrase}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    Rephrasing…
                  </>
                ) : (
                  'Rephrase with AI'
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-neutral-700">AI result</label>
              <textarea
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[120px]"
                value={result}
                readOnly
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (onApplyText) onApplyText(result);
                    onClose();
                  }}
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
                >
                  Apply to template
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIWhatsAppHelper;

